import { COMMAND_DISPLAYS } from '@/lib/mentions/auto-mention-recognition';
import type { EditImageSaveTarget } from '@/types/edit-image';

const MODEL_SPLIT_SYSTEM_PROMPT =
  '你是宫格拆分检测器。只根据参考图判断每一格的真实画面边界。' +
  '必须只输出符合 schema 的 JSON，不要 markdown、不要解释。' +
  '坐标全部使用 0 到 1 的相对值：原点在图片左上角，x 向右，y 向下。' +
  'x/y 是格子左上角，width/height 是宽高。不要带分隔线、白缝、外框。' +
  '禁止改选格子，禁止发明画面，禁止输出像素整数。';

const MODEL_SPLIT_AUTO_USER_PROMPT =
  '请自动数出图里所有独立格子，按从左到右、从上到下编号，返回每一格的真实画面边界。只返回 JSON。';

export function buildModelSplitUserPrompt(rows?: number, cols?: number): string {
  if (rows && cols) {
    return (
      `这是一张 ${rows} 行 × ${cols} 列的宫格图，一共 ${rows * cols} 格。` +
      `请按从左到右、从上到下编号，找出全部 ${rows * cols} 格的真实画面边界。` +
      '缝隙和分隔线不要包进框。只返回 JSON。'
    );
  }
  return MODEL_SPLIT_AUTO_USER_PROMPT;
}

export function buildModelSplitPromptCharCount(rows?: number, cols?: number): number {
  return MODEL_SPLIT_SYSTEM_PROMPT.length + buildModelSplitUserPrompt(rows, cols).length;
}

export function readModelSplitJobOutput(outputData: unknown): {
  panels: Array<{ index: number; x: number; y: number; width: number; height: number }>;
  rawText: string;
  creditsUsed?: number;
  sourceWidth?: number;
  sourceHeight?: number;
} {
  if (!outputData || typeof outputData !== 'object') {
    return { panels: [], rawText: '' };
  }
  const record = outputData as Record<string, unknown>;
  const rawPanels = record.panels;
  const panels: Array<{ index: number; x: number; y: number; width: number; height: number }> = [];
  if (Array.isArray(rawPanels)) {
    for (const item of rawPanels) {
      if (!item || typeof item !== 'object') continue;
      const panel = item as Record<string, unknown>;
      const index = Number(panel.index);
      const x = Number(panel.x);
      const y = Number(panel.y);
      const width = Number(panel.width);
      const height = Number(panel.height);
      if (![index, x, y, width, height].every(Number.isFinite)) continue;
      panels.push({ index, x, y, width, height });
    }
  }
  return {
    panels,
    rawText: typeof record.rawText === 'string' ? record.rawText : '',
    creditsUsed: typeof record.creditsUsed === 'number' ? record.creditsUsed : undefined,
    sourceWidth: typeof record.sourceWidth === 'number' ? record.sourceWidth : undefined,
    sourceHeight: typeof record.sourceHeight === 'number' ? record.sourceHeight : undefined,
  };
}

export function canShowStoryboardImageSplitTab(
  saveTarget: EditImageSaveTarget | null | undefined
): boolean {
  return saveTarget?.kind === 'storyboard-image';
}

export const DEFAULT_FIXED_SPLIT_GRID = { rows: 3, cols: 3 } as const;
export const DEFAULT_GRID_REPAINT_MODEL_NAME = 'gpt-image-2';
/** 仅用于词元预估，不展示给用户。与服务端 GRID_SPLIT_MODEL_DETECT 一致。 */
export const GRID_SPLIT_MODEL_DETECT_MODEL = 'gemini-3.7-flash';
export const GRID_SPLIT_DETECT_METHOD_GEOMETRY = 'geometry';
export const GRID_SPLIT_DETECT_METHOD_MODEL = 'model';

export type GridSplitDetectMethod =
  | typeof GRID_SPLIT_DETECT_METHOD_GEOMETRY
  | typeof GRID_SPLIT_DETECT_METHOD_MODEL;

export function resolveDefaultGridRepaintModelName(
  modelNames: readonly string[],
  current?: string
): string {
  if (current && modelNames.includes(current)) return current;
  if (modelNames.includes(DEFAULT_GRID_REPAINT_MODEL_NAME)) {
    return DEFAULT_GRID_REPAINT_MODEL_NAME;
  }
  return modelNames[0] ?? '';
}
export const STORYBOARD_IMAGE_SPLIT_SUBMIT_TIMEOUT_MS = 180_000;

export type StoryboardSplitGenerationSource = {
  aiParams?: Record<string, unknown> | null;
  aiPrompt?: string | null;
};

const BOARD_GRID_LAYOUT: Record<string, { rows: number; cols: number }> = {
  boardGrid: DEFAULT_FIXED_SPLIT_GRID,
  boardGrid4: { rows: 2, cols: 2 },
  boardGrid9: { rows: 3, cols: 3 },
  boardGrid12: { rows: 4, cols: 3 },
  boardGrid16: { rows: 4, cols: 4 },
  boardGrid24: { rows: 6, cols: 4 },
};

const BOARD_GRID_KEY_ORDER = [
  'boardGrid24',
  'boardGrid16',
  'boardGrid12',
  'boardGrid9',
  'boardGrid4',
  'boardGrid',
] as const;

function layoutFromBoardGridKey(value: string): { rows: number; cols: number } | null {
  return BOARD_GRID_LAYOUT[value.trim()] ?? null;
}

function layoutFromText(text: string): { rows: number; cols: number } | null {
  for (const key of BOARD_GRID_KEY_ORDER) {
    if (key !== 'boardGrid' && text.includes(key)) return BOARD_GRID_LAYOUT[key];
  }
  for (const key of BOARD_GRID_KEY_ORDER) {
    const display = COMMAND_DISPLAYS[key];
    if (display && text.includes(display)) return BOARD_GRID_LAYOUT[key];
  }
  return null;
}

function collectBoardGridLayouts(
  value: unknown,
  seen: WeakSet<object>,
  acc: Array<{ rows: number; cols: number }>
) {
  if (value == null) return;
  if (typeof value === 'string') {
    const fromKey = layoutFromBoardGridKey(value);
    if (fromKey) {
      acc.push(fromKey);
      return;
    }
    const fromText = layoutFromText(value);
    if (fromText) acc.push(fromText);
    return;
  }
  if (typeof value !== 'object') return;
  if (seen.has(value)) return;
  seen.add(value);

  if (Array.isArray(value)) {
    for (const item of value) collectBoardGridLayouts(item, seen, acc);
    return;
  }

  const record = value as Record<string, unknown>;
  for (const key of ['commandKey', 'promptId', 'display', 'aiPrompt', 'prompt']) {
    collectBoardGridLayouts(record[key], seen, acc);
  }
  for (const key of ['commandSet', 'businessParams', 'aiParams', 'generate']) {
    collectBoardGridLayouts(record[key], seen, acc);
  }
}

/** 从分镜图生成参数推断等分宫格行列；读不到则回退 3×3。 */
export function resolveFixedSplitGridFromGeneration(
  source?: StoryboardSplitGenerationSource | Record<string, unknown> | null
): { rows: number; cols: number } {
  const acc: Array<{ rows: number; cols: number }> = [];
  collectBoardGridLayouts(source, new WeakSet<object>(), acc);
  return acc[0] ?? { ...DEFAULT_FIXED_SPLIT_GRID };
}

/** 画板拆分推断用的资源切片；避免本模块依赖 canvas-snapshot 运行时。 */
export type CanvasSplitGenerationResource = {
  resourceId: string;
  prompt?: string | null;
  aiParams?: Record<string, unknown> | null;
};

function toSplitGenerationSource(
  resource: CanvasSplitGenerationResource
): StoryboardSplitGenerationSource {
  return {
    aiParams: resource.aiParams,
    aiPrompt: resource.prompt,
  };
}

/**
 * 画板打开拆分时解析生成参数：展开历史命中该图用历史，否则 snapshot 按 resourceId 精确匹配。
 * 不盲取 currentResources[0]，避免历史非定稿串到当前宫格。
 */
export function resolveCanvasSplitGenerationSource(params: {
  sourceImageId: string;
  planId: string;
  historyByResourceId: ReadonlyMap<string, CanvasSplitGenerationResource>;
  currentResourcesByPlanId: ReadonlyMap<string, readonly CanvasSplitGenerationResource[]>;
}): StoryboardSplitGenerationSource | undefined {
  const historyResource = params.historyByResourceId.get(params.sourceImageId);
  if (historyResource) return toSplitGenerationSource(historyResource);

  const current = params.currentResourcesByPlanId
    .get(params.planId)
    ?.find((resource) => resource.resourceId === params.sourceImageId);
  return current ? toSplitGenerationSource(current) : undefined;
}

export type SplitPanelProgressKind = 'pending' | 'processing' | 'completed' | 'failed' | 'unknown';

export interface SplitPanelProgress {
  panelIndex: number;
  jobId: string;
  progress: number;
  kind: SplitPanelProgressKind;
}

type SplitJobStatusSource = {
  status?: unknown;
  progress?: number;
  calculatedProgress?: number;
};

export function resolveSplitJobKind(status: unknown): SplitPanelProgressKind {
  if (status === 1 || status === 'pending') return 'pending';
  if (status === 2 || status === 'processing') return 'processing';
  if (status === 3 || status === 'completed') return 'completed';
  if (status === 4 || status === 5 || status === 'failed' || status === 'cancelled') {
    return 'failed';
  }
  return 'unknown';
}

export function isStoryboardImageSplitJobActive(status: number | string | undefined): boolean {
  const kind = resolveSplitJobKind(status);
  return kind === 'pending' || kind === 'processing';
}

function clampProgressPercent(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, Math.round(numeric)));
}

export function buildSplitPanelProgressRows(
  sessionJobIds: Record<number, string>,
  jobStatusMap: Record<string, SplitJobStatusSource | undefined>
): SplitPanelProgress[] {
  return Object.entries(sessionJobIds)
    .map(([indexText, jobId]) => {
      const live = jobStatusMap[jobId];
      const resolved = resolveSplitJobKind(live?.status);
      const kind = resolved === 'unknown' ? 'pending' : resolved;
      const raw = live?.calculatedProgress ?? live?.progress ?? 0;
      return {
        panelIndex: Number(indexText),
        jobId,
        progress: kind === 'completed' ? 100 : clampProgressPercent(raw),
        kind,
      };
    })
    .sort((left, right) => left.panelIndex - right.panelIndex);
}

export function computeSplitOverallProgress(rows: readonly SplitPanelProgress[]): {
  completedCount: number;
  totalCount: number;
  percent: number;
} {
  if (rows.length === 0) {
    return { completedCount: 0, totalCount: 0, percent: 0 };
  }
  const completedCount = rows.filter((row) => row.kind === 'completed').length;
  const percent = Math.round(rows.reduce((sum, row) => sum + row.progress, 0) / rows.length);
  return { completedCount, totalCount: rows.length, percent };
}

export function collectInFlightSplitIndexes(rows: readonly SplitPanelProgress[]): Set<number> {
  return new Set(
    rows
      .filter((row) => row.kind === 'pending' || row.kind === 'processing')
      .map((row) => row.panelIndex)
  );
}

export interface SplitSubmitBatch {
  id: string;
  startedAt: string;
  finishMode: SplitFinishMode;
  jobIds: Record<number, string>;
}

export interface SplitSubmitBatchProgress {
  id: string;
  startedAt: string;
  finishMode: SplitFinishMode;
  rows: SplitPanelProgress[];
  overall: { completedCount: number; totalCount: number; percent: number };
  locked: boolean;
}

export function appendSplitSubmitBatch(
  batches: readonly SplitSubmitBatch[],
  params: {
    jobs: readonly { panelIndex?: number; jobId: string }[];
    finishMode?: SplitFinishMode;
    startedAt: string;
    id: string;
  }
): SplitSubmitBatch[] {
  const jobIds: Record<number, string> = {};
  for (const job of params.jobs) {
    if (job.panelIndex == null || !job.jobId) continue;
    jobIds[job.panelIndex] = job.jobId;
  }
  if (Object.keys(jobIds).length === 0) return [...batches];
  return [
    {
      id: params.id,
      startedAt: params.startedAt,
      finishMode: resolveSplitFinishMode(params.finishMode),
      jobIds,
    },
    ...batches,
  ];
}

export function collectSplitBatchJobIds(batches: readonly SplitSubmitBatch[]): string[] {
  return batches.flatMap((batch) => Object.values(batch.jobIds));
}

export function buildSplitSubmitBatchProgress(
  batches: readonly SplitSubmitBatch[],
  jobStatusMap: Record<string, SplitJobStatusSource | undefined>
): SplitSubmitBatchProgress[] {
  return batches.map((batch) => {
    const rows = buildSplitPanelProgressRows(batch.jobIds, jobStatusMap);
    return {
      id: batch.id,
      startedAt: batch.startedAt,
      finishMode: batch.finishMode,
      rows,
      overall: computeSplitOverallProgress(rows),
      locked: collectInFlightSplitIndexes(rows).size > 0,
    };
  });
}

export function formatSplitBatchPanelIndexes(indexes: readonly number[]): string {
  const unique = [...new Set(indexes.filter((index) => Number.isFinite(index) && index > 0))].sort(
    (left, right) => left - right
  );
  if (unique.length === 0) return '';
  if (unique.length === 1) return `第${unique[0]}格`;
  return `第${unique.join('、')}格`;
}

export function formatSplitBatchMeta(params: {
  panelIndexes: readonly number[];
  finishMode: SplitFinishMode;
  startedAtLabel: string;
}): string {
  const panels = formatSplitBatchPanelIndexes(params.panelIndexes);
  const action = params.finishMode === SPLIT_FINISH_MODE_UPSCALE ? '超清' : '重绘';
  const startedAt = params.startedAtLabel.trim();
  return [panels, action, startedAt].filter(Boolean).join(' · ');
}

export function filterSubmittableSplitBoxes<T extends { index: number }>(
  boxes: readonly T[],
  inFlightIndexes: ReadonlySet<number>
): T[] {
  return boxes.filter((box) => !inFlightIndexes.has(box.index));
}

/**
 * 拆分页删除快捷键：是否立即删除当前编辑区域。
 * `hasModifier` 只算 meta / ctrl / alt，Shift 放行——与标注侧 shouldIgnoreMarkupObjectDeleteKey 同口径。
 */
export function shouldExecuteSplitPanelDelete(input: {
  key: string;
  isComposing: boolean;
  hasModifier: boolean;
  busy: boolean;
  deletableCount: number;
  confirmOpen: boolean;
}): boolean {
  if (input.key !== 'Delete' && input.key !== 'Backspace') return false;
  if (input.isComposing || input.hasModifier) return false;
  if (input.busy || input.confirmOpen) return false;
  return input.deletableCount > 0;
}

export function formatSplitCreditsHint(input: {
  count: number;
  unitCredits: number | undefined;
  totalCredits: number | undefined;
  isEstimating: boolean;
}): string {
  if (input.count <= 0) return '0 词元';
  if (input.isEstimating) return '计算中…';
  const { unitCredits, totalCredits, count } = input;
  if (
    unitCredits != null &&
    totalCredits != null &&
    Number.isInteger(unitCredits) &&
    unitCredits * count === totalCredits
  ) {
    return `${count} × ${unitCredits.toLocaleString()} = ${totalCredits.toLocaleString()} 词元`;
  }
  if (totalCredits != null) return `${totalCredits.toLocaleString()} 词元`;
  return '— 词元';
}

/** 未选或模型目录未就绪时的回落档，与服务端 GRID_SPLIT_SIZE / GRID_SPLIT_QUALITY 一致。 */
export const SPLIT_CREDIT_SIZE = '2K';
export const SPLIT_CREDIT_QUALITY = 'medium';

export function resolveSplitRepaintParam(
  current: string | undefined,
  allowed: readonly string[],
  fallback: string
): string {
  if (current && (allowed.length === 0 || allowed.includes(current))) return current;
  if (fallback && (allowed.length === 0 || allowed.includes(fallback))) return fallback;
  return allowed[0] ?? fallback;
}

export function buildSplitCreditsEstimateRequest(
  modelName: string,
  options?: { size?: string; quality?: string }
): {
  generationType: 'image';
  modelName: string;
  n: 1;
  size: string;
  mode: string;
  hasReference: true;
} {
  return {
    generationType: 'image',
    modelName,
    n: 1,
    size: options?.size?.trim() || SPLIT_CREDIT_SIZE,
    mode: options?.quality?.trim() || SPLIT_CREDIT_QUALITY,
    hasReference: true,
  };
}

export function scaleSplitCreditsBySelection(
  unitCredits: number | undefined,
  selectedCount: number
): number | undefined {
  if (selectedCount <= 0) return 0;
  if (unitCredits == null) return undefined;
  return unitCredits * selectedCount;
}

export const SPLIT_SAVE_MODE_HISTORY = 'plan-history' as const;
export const SPLIT_SAVE_MODE_NEW_PLAN = 'new-plan' as const;

export type SplitSaveMode = typeof SPLIT_SAVE_MODE_HISTORY | typeof SPLIT_SAVE_MODE_NEW_PLAN;

export function resolveSplitSaveMode(value: unknown): SplitSaveMode {
  return value === SPLIT_SAVE_MODE_NEW_PLAN ? SPLIT_SAVE_MODE_NEW_PLAN : SPLIT_SAVE_MODE_HISTORY;
}

export function describeSplitSaveModeHint(input: {
  saveMode: SplitSaveMode;
  count: number;
  canSaveToNewPlan: boolean;
}): string {
  if (input.saveMode === SPLIT_SAVE_MODE_NEW_PLAN && !input.canSaveToNewPlan) {
    return '暂无在当前项目中创建方案的权限';
  }
  if (input.count <= 0) return '请先保留至少一个拆分区域';
  switch (input.saveMode) {
    case SPLIT_SAVE_MODE_NEW_PLAN:
      return input.count === 1
        ? '将把这个拆分区域创建为独立图片方案并定稿'
        : `将把这 ${input.count} 个拆分区域分别创建为独立图片方案并定稿`;
    case SPLIT_SAVE_MODE_HISTORY:
      return input.count === 1
        ? '将把这个拆分区域的生成结果追加到当前方案历史，不定稿'
        : `将把这 ${input.count} 个拆分区域的生成结果追加到当前方案历史，不定稿`;
    default: {
      const exhaustive: never = input.saveMode;
      return exhaustive;
    }
  }
}

export const SPLIT_FINISH_MODE_REPAINT = 'repaint' as const;
export const SPLIT_FINISH_MODE_UPSCALE = 'upscale' as const;

export type SplitFinishMode = typeof SPLIT_FINISH_MODE_REPAINT | typeof SPLIT_FINISH_MODE_UPSCALE;

export function resolveSplitFinishMode(value: unknown): SplitFinishMode {
  return value === SPLIT_FINISH_MODE_UPSCALE
    ? SPLIT_FINISH_MODE_UPSCALE
    : SPLIT_FINISH_MODE_REPAINT;
}

export function splitUpscaleSizeKey(width: number, height: number): string {
  return `${width}x${height}`;
}

export function groupSplitBoxesBySize(
  boxes: readonly { width: number; height: number }[]
): Array<{ width: number; height: number; count: number }> {
  const groups: Array<{ width: number; height: number; count: number }> = [];
  const indexByKey = new Map<string, number>();
  for (const box of boxes) {
    const key = splitUpscaleSizeKey(box.width, box.height);
    const existing = indexByKey.get(key);
    if (existing == null) {
      indexByKey.set(key, groups.length);
      groups.push({ width: box.width, height: box.height, count: 1 });
      continue;
    }
    groups[existing].count += 1;
  }
  return groups;
}

export function sumSplitUpscaleCredits(
  groups: readonly { width: number; height: number; count: number }[],
  unitByKey: Record<string, number | undefined>
): number | undefined {
  if (groups.length === 0) return 0;
  let total = 0;
  for (const group of groups) {
    const unit = unitByKey[splitUpscaleSizeKey(group.width, group.height)];
    if (unit == null) return undefined;
    total += unit * group.count;
  }
  return total;
}

export function describeSplitSubmitResult(result: {
  jobs: readonly { panelIndex?: number }[];
  partialError?: string | null;
  saveMode?: SplitSaveMode;
  finishMode?: SplitFinishMode;
}): { title: string; description: string; destructive: boolean } {
  const count = result.jobs.length;
  const toNewPlan = resolveSplitSaveMode(result.saveMode) === SPLIT_SAVE_MODE_NEW_PLAN;
  const action =
    resolveSplitFinishMode(result.finishMode) === SPLIT_FINISH_MODE_UPSCALE ? '超清' : '重绘';
  if (result.partialError) {
    return {
      title: '部分格子已提交',
      description: toNewPlan
        ? `${count} 格已开始${action}，完成后将各建新方案。${result.partialError}`
        : `${count} 格已开始${action}。${result.partialError}`,
      destructive: true,
    };
  }
  return {
    title: toNewPlan ? '已提交到新方案' : '已提交到历史',
    description: toNewPlan
      ? `正在${action} ${count} 格，完成后各建一个新方案`
      : `正在${action} ${count} 格，完成后写入历史`,
    destructive: false,
  };
}
